<template>
  <div
    class="chat-history-list absolute top-0 right-0 bottom-0 bg-white transform transition-all duration-300 ease-in-out flex flex-col"
    :class="{ 'translate-x-0': isOpen, 'translate-x-full': !isOpen }"
  >
    <!-- Header -->
    <div class="head p-4 bg-gradient-to-r from-blue-800 to-blue-700 border-b border-blue-900 shadow-md">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <h3 class="text-xl font-bold text-white">Chat History</h3>
        </div>
        <button
          @click="$emit('close')"
          class="p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors border border-white/30 flex items-center justify-center"
          aria-label="Close chat history"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <!-- Create New Chat Button -->
    <div class="px-4 pt-4">
      <button
        @click="createNewChat"
        class="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center"
      >
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        Create New Chat
      </button>
    </div>
    
    <!-- Chat List -->
    <div class="flex-1 overflow-y-auto p-4 space-y-3">
      <div v-if="userChats.length === 0" class="text-center py-8 text-gray-500">
        <svg class="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
        </svg>
        <p>No chat history found</p>
      </div>
      
      <div 
        v-for="chat in userChats" 
        :key="chat.id" 
        @click="selectChat(chat.id)"
        class="chat-item p-4 rounded-lg cursor-pointer transition-all duration-200 hover:bg-blue-50 relative group"
        :class="{ 'bg-blue-100 border-blue-300 border': currentChatId === chat.id }"
      >
        <div class="flex justify-between items-start">
          <div>
            <h4 class="font-medium text-gray-900 mb-1 truncate pr-8">{{ chat.title }}</h4>
            <p class="text-sm text-gray-500">
              {{ formatDate(chat.last_message_date) }}
            </p>
          </div>
          <button 
            @click.stop="confirmDeleteChat(chat.id)" 
            class="text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 absolute top-4 right-4"
            aria-label="Delete chat"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-sm mx-auto">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Delete Chat</h3>
        <p class="text-gray-600 mb-6">Are you sure you want to delete this chat? This action cannot be undone.</p>
        <div class="flex justify-end space-x-3">
          <button 
            @click="showDeleteConfirm = false" 
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button 
            @click="deleteSelectedChat" 
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue';
import { ChatWrapper } from '@/types/chat';

export default defineComponent({
  name: 'ChatHistoryList',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    userChats: {
      type: Array as PropType<ChatWrapper[]>,
      default: () => []
    },
    currentChatId: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'select-chat', 'delete-chat', 'create-new-chat'],
  setup(props, { emit }) {
    const showDeleteConfirm = ref(false);
    const chatToDelete = ref<string | null>(null);
    
    const selectChat = (chatId: string) => {
      emit('select-chat', chatId);
    };
    
    const confirmDeleteChat = (chatId: string) => {
      chatToDelete.value = chatId;
      showDeleteConfirm.value = true;
    };
    
    const deleteSelectedChat = () => {
      if (chatToDelete.value) {
        emit('delete-chat', chatToDelete.value);
        showDeleteConfirm.value = false;
        chatToDelete.value = null;
      }
    };
    
    const formatDate = (date: Date) => {
      if (!date) return '';
      
      const dateObj = new Date(date);
      const now = new Date();
      const diffMs = now.getTime() - dateObj.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return `Today at ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (diffDays === 1) {
        return `Yesterday at ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return dateObj.toLocaleDateString();
      }
    };
    
    const createNewChat = () => {
      emit('create-new-chat');
    };
    
    return {
      selectChat,
      confirmDeleteChat,
      deleteSelectedChat,
      createNewChat,
      showDeleteConfirm,
      formatDate
    };
  }
});
</script>

<style scoped>

.head div {
  flex-grow: 1;
}

.chat-history-list.translate-x-0 {
  box-shadow: -5px 0 25px rgba(0, 0, 0, 0.1);
}

.head {
    display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  height: 95px;
}

.chat-history-list {
  margin-top: 0;
  border-radius: 0 0 0 8px;
  min-width: 25rem;
  max-width: 100%;
  width: max-content;
  box-shadow: -5px 0 25px transparent;
  transition: all 330ms ease-in-out;
}

.chat-item {
  border: 1px solid transparent;
}

.chat-item:hover {
  border-color: #e5e7eb;
}

.chat-item.active {
  background-color: rgba(0, 0, 145, 0.1);
  border-color: rgba(0, 0, 145, 0.2);
}
</style>